import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { StructuredFilter } from "@media-viewer/shared";

export interface QuerySnapshot {
  /** Page scope (from the route, e.g. /videos or /tags/:slug). */
  kind?: "photo" | "video";
  tag?: string;
  /**
   * The active search filter. Single source of truth for query-mode bulk
   * actions, so "select all matching" resolves the exact set the grid shows —
   * including fuzzy multi-tag OR groups. Null when only page scope is active.
   */
  structured?: StructuredFilter | null;
}

export type SelectionMode =
  | { kind: "ids"; ids: Set<number> }
  | { kind: "query"; snapshot: QuerySnapshot; matched: number };

interface SelectionApi {
  active: boolean;
  mode: SelectionMode;
  /** Last clicked index — used for shift-range (only meaningful in ids mode). */
  lastIndex: number | null;
  /** Ordered ids currently rendered in the grid. */
  visibleIds: number[];
  /** Total items the selection covers — `ids.size` or `matched`. */
  count: number;
  isQueryMode: boolean;
  isSelected: (id: number) => boolean;
  /**
   * Returns the id array for endpoints that take an id list, or `null`
   * when in query mode (caller should branch to the *-by-query endpoint).
   */
  getIdsForMutation: () => number[] | null;
  setActive: (on: boolean) => void;
  toggle: (id: number, opts?: { index?: number; shift?: boolean }) => void;
  selectAll: () => void;
  selectAllMatching: (snapshot: QuerySnapshot, matched: number) => void;
  clear: () => void;
  setVisibleIds: (ids: number[]) => void;
}

const Ctx = createContext<SelectionApi | null>(null);

const EMPTY_IDS_MODE: SelectionMode = { kind: "ids", ids: new Set() };

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [active, setActiveState] = useState(false);
  const [mode, setMode] = useState<SelectionMode>(EMPTY_IDS_MODE);
  const [lastIndex, setLastIndex] = useState<number | null>(null);
  const [visibleIds, setVisibleIdsState] = useState<number[]>([]);

  const clear = useCallback(() => {
    setMode({ kind: "ids", ids: new Set() });
    setLastIndex(null);
  }, []);

  const setActive = useCallback(
    (on: boolean) => {
      setActiveState(on);
      if (!on) clear();
    },
    [clear],
  );

  const setVisibleIds = useCallback((ids: number[]) => {
    // Bail when the id list is unchanged. Callers (e.g. MediaGrid) pass a
    // freshly-built array every render, so an unconditional setState here
    // re-renders the context → caller → new array → loop. Returning the
    // previous reference when equal lets React skip the update.
    setVisibleIdsState((prev) =>
      prev.length === ids.length && prev.every((v, i) => v === ids[i]) ? prev : ids,
    );
  }, []);

  const toggle = useCallback(
    (id: number, opts: { index?: number; shift?: boolean } = {}) => {
      setMode((cur) => {
        if (cur.kind === "query") return cur; // toggle is a no-op in query mode
        const next = new Set(cur.ids);
        if (opts.shift && lastIndex != null && opts.index != null && visibleIds.length > 0) {
          const a = Math.min(lastIndex, opts.index);
          const b = Math.max(lastIndex, opts.index);
          for (let i = a; i <= b; i++) {
            const vid = visibleIds[i];
            if (vid != null) next.add(vid);
          }
          return { kind: "ids", ids: next };
        }
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { kind: "ids", ids: next };
      });
      if (opts.index != null) setLastIndex(opts.index);
    },
    [lastIndex, visibleIds],
  );

  const selectAll = useCallback(() => {
    setMode({ kind: "ids", ids: new Set(visibleIds) });
  }, [visibleIds]);

  const selectAllMatching = useCallback((snapshot: QuerySnapshot, matched: number) => {
    setMode({ kind: "query", snapshot, matched });
  }, []);

  const isSelected = useCallback(
    (id: number) => {
      if (mode.kind === "query") return true;
      return mode.ids.has(id);
    },
    [mode],
  );

  const getIdsForMutation = useCallback((): number[] | null => {
    if (mode.kind === "query") return null;
    return Array.from(mode.ids);
  }, [mode]);

  // Esc exits select mode.
  useEffect(() => {
    if (!active) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      setActive(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, setActive]);

  const count = mode.kind === "query" ? mode.matched : mode.ids.size;
  const isQueryMode = mode.kind === "query";

  const value = useMemo<SelectionApi>(
    () => ({
      active,
      mode,
      lastIndex,
      visibleIds,
      count,
      isQueryMode,
      isSelected,
      getIdsForMutation,
      setActive,
      toggle,
      selectAll,
      selectAllMatching,
      clear,
      setVisibleIds,
    }),
    [
      active,
      mode,
      lastIndex,
      visibleIds,
      count,
      isQueryMode,
      isSelected,
      getIdsForMutation,
      setActive,
      toggle,
      selectAll,
      selectAllMatching,
      clear,
      setVisibleIds,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSelection(): SelectionApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSelection must be used within SelectionProvider");
  return v;
}
